import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { AlertTriangle, Ban, CalendarClock, ChevronLeft, CreditCard, LogOut, MessageSquare, MoreHorizontal, Plus, QrCode, Receipt, Search, UserPlus, Users, Utensils, X, } from "lucide-react"
import QRCode from "react-qr-code"
import type { MenuGridCategory, MenuGridItem } from "@/components/features/menu/menu-grid"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { MainContainer } from "@/components/ui/container"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, } from "@/components/ui/dropdown-menu"
import { DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, ResponsiveDrawer, } from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
    ALERT_PULSE_CLASS,
    alertMessageFor,
    billStatusDot,
    operationalStatusDot,
    operationalStatusLabelKey,
    staffMutationResourceKey,
    tableIsOccupied,
    type AlertReason,
    type OperationalStatus,
} from "@/features/staff/table-status"
import { OrderItemDrawer, useOrderDraft } from "@/features/staff/order-item-drawer"
import {
    CashDrawerClosedNotice,
    cashDrawerBlockedReason,
    cashDrawerBlocksOrders,
    useCashDrawer,
} from "@/features/staff/cash-drawer"
import {
    DiscountAction,
    DiscountBlockedNotice,
    DiscountPendingStrip,
    DiscountVoidButton,
} from "@/features/staff/discount-action"
import { discountedLine, discountSourceLabel, systemDiscount, tDiscount, type AppliedDiscount, type DiscountEligibility, type DiscountKindOption, type DiscountReasonOption, } from "@/features/staff/discounts"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import { billCancelPayload, CANCEL_PATHS, type CancellationReasonOption } from "@/features/staff/cancellations"
import { billReopenPayload, REOPEN_PATHS, type ReopenReasonOption } from "@/features/staff/reopenings"
import { RefundAction } from "@/features/staff/refund-action"
import type { IssuedRefund, RefundEligibility, RefundReasonOption } from "@/features/staff/refunds"
import { EntryRequestPanel, type StaffEntryRequest } from "@/features/staff/entry-request-panel"
import { paymentTone } from "@/lib/status-colors"
import { cn } from "@/lib/utils"
import { intlLocale, t } from "@/lib/i18n"
import { normalizeForSearch } from "@/lib/search"
import { patchInline, postInline, putInline, toastInlineUpdateError, toastInlineUpdateSuccess, type InlineUpdateResult } from "@/lib/inline-update"
export type TableAccountOrder = {
    id: number
    name: string
    quantity: number
    status: string
    statusLabel: string
    serviceType: string
    serviceTypeLabel: string
    lineTotal: string
    /**
     * `unpaid`, `locked` or `paid`. Optional so a payload from an older
     * serializer treats every line as pickable rather than blanking the panel.
     */
    paymentStatus?: string
    paymentStatusLabel?: string
    /**
     * What the line cost before anything came off it. `lineTotal` is now the
     * charged figure, so this is the only place the original price survives —
     * and `Location::DiscountPolicy` rule 3 requires it stay on the ticket.
     * Optional so a payload from an older serializer renders the line as it
     * always did instead of blanking the panel.
     */
    grossLineTotal?: string
    /** What came off, as a decimal string. `0.00` on an undiscounted line. */
    discountAmount?: string
    /** The live discount row, pending or applied. Null when there is none. */
    discount?: AppliedDiscount | null
    /** Whether this line can take a discount, straight from the server. */
    discountEligibility?: DiscountEligibility | null
    currency: string
    notes: string | null
    customerName: string
    billId: number
    billLabel: string
    billStatus: string
    billStatusLabel: string
}
type TableAccountPendingPaymentAttempt = {
    id: number
    status: string
    paymentMethod: string
    totalAmount: string
    selectedOrderIds: number[]
    payerName?: string | null
    isShare?: boolean
    createdAt?: string
}
type TableAccountSettledPayment = {
    id: number
    paymentMethod: string
    payerName: string | null
    subtotal: string
    tipAmount: string
    totalAmount: string
    isShare: boolean
    completedAt: string | null
    /**
     * Whether this charge can be given back, straight from
     * `Location::RefundEligibility`. Optional so a payload rendered by an older
     * serializer degrades to "no refund action" instead of blanking the table
     * on `refundEligibility.allowed` of undefined.
     */
    refundEligibility?: RefundEligibility | null
    /** Every refund already filed against this charge, failures included. */
    refunds?: IssuedRefund[]
}
type TableAccountSplit = {
    id: number
    payerCount: number
    setTotal: string
    collected: string
    outstanding: string
    payersRemaining: number
    settling: boolean
}
type TableAccountBillAllowedActions = {
    canClose: boolean
    canCreatePaymentAttempt: boolean
    canReopen: boolean
    canCancel: boolean
    canResolvePendingAttempt: boolean
    canCancelSplit: boolean
    /** `Location::User#can_apply_discounts?` crossed with this bill's state. */
    canApplyDiscount?: boolean
    /** `can_authorize_discounts?`: who may resolve what a waiter asked for. */
    canAuthorizeDiscount?: boolean
}
export type TableAccountBill = {
    id: number
    label: string
    status: string
    statusLabel: string
    canClose: boolean
    pendingPaymentAttempt: TableAccountPendingPaymentAttempt | null
    pendingPaymentAttempts: TableAccountPendingPaymentAttempt[]
    settledPayments: TableAccountSettledPayment[]
    split: TableAccountSplit | null
    allowedActions: TableAccountBillAllowedActions
    ordersCount: number
    total: string
    /**
     * How `total` divides between collected and still owed. The two are exactly
     * complementary by construction on the server, so the panel can render
     * either without recomputing the other. Optional: an older payload falls
     * back to charging `total`, which is what this screen did before.
     */
    paidTotal?: string
    outstandingTotal?: string
    /** Everything taken off this bill, already reflected in `total`. */
    discountTotal?: string
    /** What a waiter asked for and no manager has decided yet. */
    pendingDiscounts?: AppliedDiscount[]
    /** The bill-level answer, with no line attached. */
    discountEligibility?: DiscountEligibility | null
    currency: string
    orders: TableAccountOrder[]
}
// One account per table: no target picker, no "open another one". Dividing the
// bill happens two ways, both on top of this one account: a diner opens an
// equal-share split (`Location::BillSplit`), or staff picks lines and charges
// only those.
type TableAccountWorkspacePayload = {
    billCount: number
    orderCount: number
    visibleBills: TableAccountBill[]
}
type OrderErrors = Record<string, string | string[] | undefined>
export type TableDiner = {
    id: number
    name: string
    initials: string
    billId: number | null
    joinedAt: string
}
type TableContext = {
    id: number
    externalId: string
    name: string
    seats: number
    status: string
    statusLabel: string
    operationalStatus: OperationalStatus
    alert: boolean
    /** Every reason the table needs a human, most urgent first. `alert` is "any of these". */
    alertReasons: AlertReason[]
    activeDinersCount: number
    diners: TableDiner[]
    activeInvite: ActiveInvite | null
    /** Pending requests to be let in, most-recent-last. Empty when the door is quiet. */
    entryRequests: StaffEntryRequest[]
    /**
     * The booking holding this table, soonest first. Null when nobody booked it
     * and on every table while `loyalty` is off.
     */
    nextReservation: TableReservation | null
}
export type TableReservation = {
    id: number
    guestName: string
    guestPhone: string | null
    partySize: number
    status: string
    startsAt: string
    endsAt: string
}
type ActiveInvite = {
    id: number
    token: string
    expiresAt: string
    maxUses: number
    usedCount: number
    url: string
}
export type TableDetailAction = {
    label: string
    value: string
    disabled: boolean
    primary: boolean
    kind: "navigate" | "event" | "invite"
    href?: string
    event?: string
    tableExternalId?: string
}
export type TableSwitcherItem = {
    id: number
    externalId: string
    name: string
    status: string
    statusLabel: string
    operationalStatus: OperationalStatus
    alert: boolean
}
type Props = {
    table: TableContext
    categories: MenuGridCategory[]
    orphanItems: MenuGridItem[]
    workspace: TableAccountWorkspacePayload
    actions: TableDetailAction[]
    allTables: TableSwitcherItem[]
    orderErrors?: OrderErrors[]
    /** The refund catalog, straight from `Location::RefundPolicy`. */
    refundReasons?: RefundReasonOption[]
    /**
     * Whether this staff member may issue a refund here at all: the `refunds`
     * feature is on for the location AND they are a manager
     * (`Location::User#can_issue_refunds?`). Hiding the control is not the
     * guard — `Staff::Location::RefundsController` refuses either way — it
     * keeps the floor from being offered an action that bounces them to the
     * dashboard mid-service.
     */
    canRefund?: boolean
    /** The reason catalog, straight from `Location::DiscountPolicy.options`. */
    discountReasons?: DiscountReasonOption[]
    /** The kind catalog, from `Location::DiscountPolicy.kind_options`. */
    discountKinds?: DiscountKindOption[]
    /**
     * Whether this staff member may ask for a discount here at all: the
     * `discounts` feature is on for the location AND they hold a role that may
     * apply one (`Location::User#can_apply_discounts?` — manager, waiter or
     * cashier). Hiding the control is not the guard; the controller refuses
     * either way. It keeps the floor from being offered an action that bounces
     * them to the dashboard mid-service.
     */
    canDiscount?: boolean
    /** Whether they may resolve someone else's pending discount. Manager-only. */
    canAuthorizeDiscount?: boolean
    /** The closed cancellation catalog, straight from `Location::CancellationPolicy.options`. */
    cancellationReasons?: CancellationReasonOption[]
    reopenReasons?: ReopenReasonOption[]
    /** The location's IANA zone. A refund is timestamped where the restaurant is. */
    timeZone?: string
    /**
     * `Location#feature_enabled?(:dynamic_invites)`. Off by default (#155 phase
     * 4): the printed static QR is the MVP's door, so every waiter-issued-QR
     * control on this workspace is hidden rather than offered and refused.
     */
    dynamicInvitesOn?: boolean
}
export function paymentDisplayAmount(total: string | number): number {
    const amount = typeof total === "number" ? total : Number(total)
    return Number.isFinite(amount) ? amount : 0
}
/**
 * Whether staff may pick this line for a partial charge.
 *
 * `locked` is excluded along with `paid`, and it has to be: the line belongs to
 * a charge already in flight, and `PaymentAttemptCreationService` refuses it
 * with `items_locked_for_payment`. A checkbox the server then rejects is worse
 * than no checkbox — the tick promises something the confirm cannot deliver.
 *
 * A payload that predates `paymentStatus` treats every uncancelled line as
 * pickable, which is what this screen assumed before the field existed.
 */
export function lineIsSelectable(order: Pick<TableAccountOrder, "status" | "paymentStatus">): boolean {
    if (order.status === "cancelled")
        return false
    return (order.paymentStatus ?? "unpaid") === "unpaid"
}
/**
 * The selection as it stands RIGHT NOW, intersected against what is still
 * pickable, in the bill's own order.
 *
 * The stored set is never trusted on its own, and that is the whole design.
 * Realtime re-reads this workspace: a diner can settle a line from their phone
 * while a waiter has it ticked here. Charging the raw set would then be refused
 * by the server with `items_already_paid` and no explanation on screen.
 * Intersecting makes the tick disappear by itself, which is what the waiter
 * would expect anyway.
 */
export function reconcileSelection(selected: ReadonlySet<number>, orders: TableAccountOrder[]): number[] {
    return orders.filter((order) => lineIsSelectable(order) && selected.has(order.id)).map((order) => order.id)
}
/** What the selected lines add up to, for the amount on the charge button. */
export function selectionSubtotal(orders: TableAccountOrder[], selectedIds: number[]): number {
    const picked = new Set(selectedIds)
    return orders.reduce((sum, order) => picked.has(order.id) ? sum + paymentDisplayAmount(order.lineTotal) : sum, 0)
}
export function tableDetailSummary(table: Pick<TableContext, "seats" | "activeInvite">, workspace: Pick<TableAccountWorkspacePayload, "billCount" | "orderCount">) {
    return {
        seats: table.seats,
        billCount: workspace.billCount,
        orderCount: workspace.orderCount,
        hasActiveInvite: table.activeInvite !== null,
    }
}
function formatPrice(price: string | number, currency: string) {
    const amount = paymentDisplayAmount(price)
    return new Intl.NumberFormat(intlLocale(), {
        style: "currency",
        currency: currency.toUpperCase(),
    }).format(amount)
}
function buildIdempotencyKey() {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID()
    }
    return `pa_${Date.now()}_${Math.random().toString(36).slice(2)}`
}
function paymentMethodLabel(paymentMethod: string): string {
    const labels: Record<string, string> = {
        cash: t("ui.payments.methods.short.cash"),
        physical_card_terminal: t("ui.payments.methods.short.physical_card_terminal"),
        digital_in_app: t("ui.payments.methods.short.digital_in_app"),
    }
    return labels[paymentMethod] ?? paymentMethod
}
function tableActionLabel(action: TableDetailAction): string {
    const translated = t(`ui.tables.actions.${action.value}`)
    return translated === `ui.tables.actions.${action.value}` ? action.label : translated
}
function triggerTableAction(action: TableDetailAction) {
    if (action.disabled)
        return
    if (action.kind === "navigate" && action.href) {
        router.visit(action.href, { preserveScroll: true })
        return
    }
    if (action.kind === "invite" && action.href) {
        return postInline(action.href, { table_id: action.tableExternalId })
    }
    if (action.kind === "event" && action.href && action.event) {
        return putInline(action.href, { event: action.event })
    }
}
// The categorised grid and the orphan list search the same fields, so the
// predicate lives in one place rather than twice — two copies is how the two
// lists end up disagreeing about what a term matches. `normalizedQuery` is
// already folded by the caller: fold once per keystroke, not once per dish.
export function menuItemMatches(item: Pick<MenuGridItem, "name" | "description">, normalizedQuery: string) {
    if (!normalizedQuery)
        return true
    if (normalizeForSearch(item.name).includes(normalizedQuery))
        return true
    if (item.description && normalizeForSearch(item.description).includes(normalizedQuery))
        return true
    return false
}
function StatusDotLabel({ status, label, className }: {
    status: OperationalStatus
    label: string
    className?: string
}) {
    return (<span className={cn("inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground", className)}>
      <span className={cn("h-2 w-2 shrink-0 rounded-full", operationalStatusDot(status))}/>
      {label}
    </span>)
}
export function TableDetailWorkspace({ table, categories, orphanItems, workspace, actions, allTables, orderErrors, refundReasons = [], canRefund = false, discountReasons = [], discountKinds = [], canDiscount = false, canAuthorizeDiscount = false, cancellationReasons = [], reopenReasons = [], timeZone, dynamicInvitesOn = true, }: Props) {
    const [summaryOpen, setSummaryOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    // Read off the shared prop rather than passed down: every screen in the
    // domain gets it from Staff::Location::BaseController, and threading it
    // through the page would be one more prop a page eventually forgets to send.
    const cashDrawerState = useCashDrawer()
    const cashDrawerBlocked = cashDrawerBlocksOrders(cashDrawerState)
    const cashDrawerReason = cashDrawerBlockedReason(cashDrawerState)
    const drawer = useOrderDraft(submitting)
    const submitLockRef = useRef(false)
    const actionLocksRef = useRef(new Set<string>())
    const [processingActionKeys, setProcessingActionKeys] = useState<Set<string>>(() => new Set())
    const [query, setQuery] = useState("")
    const [categoryFilter, setCategoryFilter] = useState<string>("all")
    const [orderTab, setOrderTab] = useState<"active" | "history">("active")
    const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<"menu" | "account">("menu")
    // The menu steps back while the table is settling up. Taking an order and
    // collecting are different jobs and the screen used to give them the same
    // fixed split, which left the account — where the money and the mistakes are
    // — in the narrowest column on screen for the whole shift. It comes back on
    // request, because a table that is paying can still order one more coffee.
    const [menuReopened, setMenuReopened] = useState(false)
    const openBills = useMemo(() => workspace.visibleBills.filter((bill) => bill.status === "open"), [workspace.visibleBills])
    const closedBills = useMemo(() => workspace.visibleBills.filter((bill) => bill.status !== "open"), [workspace.visibleBills])
    // One account per table, so there is nothing to pick: the open bill IS the
    // table's account. `openBills` stays a filter rather than a lookup because
    // history lives in the same payload.
    const activeBill = openBills[0] ?? null
    // "Abiertas" is only the right landing tab when something is open. A table
    // whose bills are all closed — a disabled one that still owes money, say —
    // would otherwise greet the waiter with "no active bill" while the unpaid
    // one sits behind Historial. Keyed on the table too: switching tables
    // reuses this component instead of remounting it, so without that the next
    // table inherits this one's tab.
    useEffect(() => {
        if (openBills.length > 0 || closedBills.length === 0)
            return
        setOrderTab("history")
    }, [table.id, openBills.length, closedBills.length])
    useEffect(() => {
        setMenuReopened(false)
    }, [table.id])
    const allItems = useMemo(() => [...categories.flatMap((category) => category.items), ...orphanItems], [categories, orphanItems])
    const selectedItem = drawer.draft.item
    const currentErrors = orderErrors?.[0] ?? {}
    // Service is gated on occupancy, not on the raw DB status: the waiter opens
    // the table first, then loads it. Ordering onto a table nobody sat at was how
    // empty tables ended up with bills.
    const tableInService = tableIsOccupied(table.operationalStatus)
    // The one phase where the middle pane changes hands. `paying` is the server's
    // own reading — a closed bill or a payment attempt in flight — so the layout
    // follows the table rather than a guess made here.
    const tableIsSettlingUp = table.operationalStatus === "paying"
    const showsMenu = tableInService && (!tableIsSettlingUp || menuReopened)
    // A table can leave service still owing money: `disable` only refuses open
    // bills, so a closed unpaid one survives it, and `derive_operational_status`
    // short-circuits on "disabled" before it would ever say "paying". The bill
    // panel is the only way to settle that from here, so it outlives the menu.
    const showAccountOutOfService = !tableInService && workspace.visibleBills.length > 0
    const cartCountByItemId = useMemo(() => {
        const counts: Record<number, number> = {}
        for (const bill of workspace.visibleBills) {
            for (const order of bill.orders) {
                const existingItem = allItems.find((item) => item.name === order.name)
                if (!existingItem)
                    continue
                counts[existingItem.id] = (counts[existingItem.id] ?? 0) + order.quantity
            }
        }
        return counts
    }, [workspace.visibleBills, allItems])
    const activeOrders = activeBill?.orders ?? []
    // What is left to collect, not what the ticket says. On a bill nobody has
    // paid into the two are the same number; once staff charges part of it they
    // diverge, and the button has to name the smaller one or it offers to charge
    // money the restaurant already took. Falls back to `total` for a payload
    // that predates the split figures.
    const activeAmount = paymentDisplayAmount(activeBill?.outstandingTotal ?? activeBill?.total ?? 0)
    const currency = activeBill?.currency ?? "MXN"
    const filteredCategories = useMemo(() => {
        const q = normalizeForSearch(query.trim())
        return categories
            .map((category) => ({
            ...category,
            items: category.items.filter((item) => menuItemMatches(item, q)),
        }))
            .filter((category) => {
            if (categoryFilter !== "all" && String(category.id) !== categoryFilter)
                return false
            return category.items.length > 0
        })
    }, [categories, query, categoryFilter])
    const filteredOrphanItems = useMemo(() => {
        const q = normalizeForSearch(query.trim())
        if (categoryFilter !== "all" && categoryFilter !== "orphan")
            return []
        if (!q)
            return orphanItems
        return orphanItems.filter((item) => menuItemMatches(item, q))
    }, [orphanItems, query, categoryFilter])
    const totalFilteredItems = filteredCategories.reduce((sum, category) => sum + category.items.length, 0) +
        filteredOrphanItems.length
    // No occupancy guard here: the menu only mounts while the table is in
    // service, so an "the table is disabled" toast could never fire and would
    // name the wrong reason if it did.
    const handleMenuItemClick = (item: MenuGridItem) => drawer.openWith(item)
    const handleSubmit = () => {
        if (submitLockRef.current)
            return
        // The last stop before the POST, not the guard: the drawer's button is
        // already disabled and the server refuses regardless. This is what keeps
        // a keyboard submit or a stale render from spending a round trip to be
        // told the same thing.
        if (cashDrawerBlocked) {
            toast.error(cashDrawerReason ?? "")
            return
        }
        if (!selectedItem) {
            toast.error(t("ui.react.features.staff.table_detail_workspace.select_item_menu"))
            return
        }
        submitLockRef.current = true
        setSubmitting(true)
        postInline("/orders", {
            source: "tables",
            context_table_external_id: table.externalId,
            orders: [
                {
                    item_id: selectedItem.id,
                    extra_ids: drawer.draft.extraIds,
                    quantity: drawer.draft.quantity,
                    notes: drawer.draft.notes,
                    service_type: "eat_in",
                    table_id: table.externalId,
                    // No bill_id, no create_new_bill: OrderBuilder#resolve_bill
                    // lands on the table's single active bill, opening one only
                    // when the table has none.
                },
            ],
        }, { preserveState: true })
            .then((result) => {
                drawer.setOpen(false)
                drawer.reset()
                // A 422 with the order errors bag also resolves (the errors render
                // inline in the workspace), so only toast when a flash arrived.
                if (result.message)
                    toastInlineUpdateSuccess(result)
            })
            .catch((error: unknown) => toastInlineUpdateError(error))
            .finally(() => {
                submitLockRef.current = false
                setSubmitting(false)
            })
    }
    const runResourceAction = (resourceKey: string, action: () => Promise<InlineUpdateResult>) => {
        if (actionLocksRef.current.has(resourceKey))
            return
        actionLocksRef.current.add(resourceKey)
        setProcessingActionKeys((current) => new Set(current).add(resourceKey))
        action()
            .then((result) => toastInlineUpdateSuccess(result))
            .catch((error: unknown) => toastInlineUpdateError(error))
            .finally(() => {
                actionLocksRef.current.delete(resourceKey)
                setProcessingActionKeys((current) => {
                    const next = new Set(current)
                    next.delete(resourceKey)
                    return next
                })
            })
    }
    const handleCloseBill = (billId: number) => {
        runResourceAction(staffMutationResourceKey("bill", billId), () => postInline(`/bills/${billId}/close`, {}, { preserveState: true }))
    }
    // `orderIds` empty means "no selection", and the ids are then left OUT of the
    // request entirely rather than sent as `[]`. That is deliberate: the service
    // reads a missing selection as "everything still unpaid on this bill", so the
    // charge-the-whole-table path stays the exact same code it always was.
    const handleCreatePaymentAttempt = (billId: number, paymentMethod: "cash" | "physical_card_terminal", orderIds: number[] = []) => {
        runResourceAction(staffMutationResourceKey("bill", billId), () => postInline(`/bills/${billId}/payment_attempts`, {
            payment_method: paymentMethod,
            tip_percentage: 0,
            idempotency_key: buildIdempotencyKey(),
            ...(orderIds.length > 0 ? { order_ids: orderIds } : {}),
        }, { preserveState: true }))
    }
    const handleResolvePaymentAttempt = (paymentAttemptId: number, status: "succeeded" | "failed") => {
        runResourceAction(staffMutationResourceKey("attempt", paymentAttemptId), () => patchInline(`/payment_attempts/${paymentAttemptId}`, { status }, { preserveState: true }))
    }
    const resourceIsLoading = (resourceKey: string) => processingActionKeys.has(resourceKey)
    const handleTableAction = (action: TableDetailAction) => {
        if (action.disabled)
            return
        if (action.kind === "navigate") {
            triggerTableAction(action)
            return
        }
        if (action.kind === "invite" && !action.href)
            return
        if (action.kind === "event" && (!action.href || !action.event))
            return
        runResourceAction(staffMutationResourceKey("table", table.id), () => {
            const result = triggerTableAction(action) as Promise<InlineUpdateResult>
            // The QR this action produces only renders inside the summary
            // drawer, and "Abrir con QR" on the unoccupied panel fires it with
            // the drawer shut — the waiter would get a toast and no QR. Open it
            // as the invite lands, which is also where the link to share is.
            if (action.value !== "invite")
                return result
            return result.then((inviteResult) => {
                setSummaryOpen(true)
                return inviteResult
            })
        })
    }
    const handleAcknowledgeWaiterCall = () => {
        runResourceAction(staffMutationResourceKey("table", table.id), () => postInline(`/tables/${table.externalId}/acknowledge_waiter_call`, {}, { preserveState: true }))
    }
    // Seating pulls the booking's start time back to now and occupies the table
    // in one server-side step (Location::Reservation#seat!), which is why this is
    // the reservation's own endpoint rather than the table's `occupy` event: the
    // party has to end up attached to the table, not merely sat at it.
    //
    // It shares the table's mutation lock, so it cannot race the occupy button
    // sitting right below it.
    const handleSeatReservation = (reservation: TableReservation) => {
        runResourceAction(staffMutationResourceKey("table", table.id), () => patchInline(`/reservations/${reservation.id}/seat`, {}, { preserveState: true }))
    }
    const tableIsLoading = resourceIsLoading(staffMutationResourceKey("table", table.id))
    const quickActions = actions
        .filter((action) => action.value !== "orders/new")
        .filter((action) => dynamicInvitesOn || action.value !== "invite")
    return (<MainContainer size="md">
      <div className="space-y-4 pb-6" data-testid={`table-detail-workspace-${table.id}`}>
      <OrderItemDrawer open={drawer.open} onOpenChange={drawer.handleOpenChange} draft={drawer.draft} onDraftChange={drawer.setDraft} onSubmit={handleSubmit} submitting={submitting} errors={currentErrors} blockedReason={cashDrawerReason} testIdPrefix="table-detail"/>

      <TableSummaryDrawer open={summaryOpen} onOpenChange={setSummaryOpen} table={table} workspace={workspace} actions={quickActions} onAction={handleTableAction} isMutating={tableIsLoading} dynamicInvitesOn={dynamicInvitesOn}/>

      <TableWorkspaceHeader table={table} tables={allTables} summaryIsOpen={summaryOpen} onOpenSummary={() => setSummaryOpen(true)}/>

      {/* Above the menu, not inside it. The account panel below stays fully
          usable with the drawer shut -- a table can still be closed, cancelled
          and read -- so the notice belongs to the screen rather than to the one
          column it takes controls away from. Renders nothing where the
          restaurant keeps no register. */}
      <CashDrawerClosedNotice/>

      {tableInService ? (<Tabs value={showsMenu ? mobileWorkspaceTab : "account"} onValueChange={(value) => setMobileWorkspaceTab(value as "menu" | "account")} className="gap-3">
        <div className={cn("grid min-h-[34rem] grid-cols-1 items-stretch gap-4 lg:h-[calc(100vh-11rem)]", showsMenu
            ? "lg:grid-cols-[244px_minmax(0,1fr)_340px]"
            : "lg:grid-cols-[244px_minmax(0,1fr)]")}>
          {/* Desktop only. On a phone the same reading lives one tap away in the
              summary drawer the header still opens — a 244px column on a 390px
              screen would take the space the work needs. */}
          <TableContextRail table={table} workspace={workspace} actions={quickActions} onAction={handleTableAction} isMutating={tableIsLoading} dynamicInvitesOn={dynamicInvitesOn} menuIsAway={!showsMenu} onReopenMenu={() => setMenuReopened(true)}/>

          {showsMenu ? (<TabsList className="grid w-full grid-cols-2 self-start lg:hidden" aria-label={t("ui.react.features.staff.table_detail_workspace.workspace_sections")}>
            <TabsTrigger value="menu">{t("ui.react.features.staff.table_detail_workspace.menu")}</TabsTrigger>
            <TabsTrigger value="account">{t("ui.react.features.staff.table_detail_workspace.account")}</TabsTrigger>
          </TabsList>) : (<Button type="button" variant="outline" className="w-full self-start lg:hidden" onClick={() => setMenuReopened(true)} data-testid="table-detail-reopen-menu-mobile">
            <Plus className="size-4"/>{t("ui.react.features.staff.table_detail_workspace.add_something_else")}
          </Button>)}

          {showsMenu ? (<TabsContent value="menu" forceMount className="m-0 min-h-0 min-w-0 overflow-hidden data-[state=inactive]:hidden lg:data-[state=inactive]:block">
            <TableMenuBrowser categories={filteredCategories} orphanItems={filteredOrphanItems} allCategories={categories} query={query} onQueryChange={setQuery} categoryFilter={categoryFilter} onCategoryFilterChange={setCategoryFilter} cartCounts={cartCountByItemId} onItemClick={handleMenuItemClick} totalItems={totalFilteredItems}/>
          </TabsContent>) : null}

          <TabsContent value="account" forceMount className="m-0 min-h-0 min-w-0 overflow-hidden data-[state=inactive]:hidden lg:data-[state=inactive]:block">
            <TableOrderPanel tab={orderTab} onTabChange={setOrderTab} closedBills={closedBills} activeBill={activeBill} openBills={openBills} activeOrders={activeOrders} amount={activeAmount} currency={currency} alertReasons={table.alertReasons ?? []} onAcknowledgeWaiterCall={handleAcknowledgeWaiterCall} tableIsMutating={tableIsLoading} resourceIsLoading={resourceIsLoading} onCloseBill={handleCloseBill} onPay={handleCreatePaymentAttempt} onResolvePayment={handleResolvePaymentAttempt} refundReasons={refundReasons} canRefund={canRefund} discountReasons={discountReasons} discountKinds={discountKinds} canDiscount={canDiscount} canAuthorizeDiscount={canAuthorizeDiscount} cancellationReasons={cancellationReasons} reopenReasons={reopenReasons} timeZone={timeZone}/>
          </TabsContent>
        </div>
      </Tabs>) : (<div className={cn("grid min-h-[34rem] grid-cols-1 items-stretch gap-4 lg:h-[calc(100vh-11rem)]", showAccountOutOfService
            ? "lg:grid-cols-[244px_minmax(0,1fr)_360px]"
            : "lg:grid-cols-[244px_minmax(0,1fr)]")}>
        {/* The rail mounts out of service too, and it has to. The summary
            trigger in the header is `lg:hidden` on the premise that the rail is
            already on screen from `lg` up; when the rail was in-service-only,
            that premise was false for a free, reserved or disabled table and
            Liberar / Reservar / Deshabilitar had no surface at all on desktop.
            The unoccupied panel carries the one call to action, never the rest.
            The menu is not merely away here, it is closed by design until the
            table is opened, so nothing offers to bring it back. */}
        <TableContextRail table={table} workspace={workspace} actions={quickActions} onAction={handleTableAction} isMutating={tableIsLoading} dynamicInvitesOn={dynamicInvitesOn} menuIsAway={false} onReopenMenu={() => setMenuReopened(true)}/>

        <TableUnoccupiedPanel status={table.operationalStatus} actions={quickActions} onAction={handleTableAction} isMutating={tableIsLoading} hasOutstandingBills={showAccountOutOfService} dynamicInvitesOn={dynamicInvitesOn} reservation={table.nextReservation} onSeatReservation={handleSeatReservation}/>
        {/* The second mount, and the one a new prop gets forgotten on: a table
            out of service still carries the bill everyone can see, so every
            discount prop has to arrive here too. `refundReasons` was missed
            exactly once, and the refund select rendered empty on this path. */}
        {showAccountOutOfService ? (<TableOrderPanel tab={orderTab} onTabChange={setOrderTab} closedBills={closedBills} activeBill={activeBill} openBills={openBills} activeOrders={activeOrders} amount={activeAmount} currency={currency} alertReasons={table.alertReasons ?? []} onAcknowledgeWaiterCall={handleAcknowledgeWaiterCall} tableIsMutating={tableIsLoading} resourceIsLoading={resourceIsLoading} onCloseBill={handleCloseBill} onPay={handleCreatePaymentAttempt} onResolvePayment={handleResolvePaymentAttempt} refundReasons={refundReasons} canRefund={canRefund} discountReasons={discountReasons} discountKinds={discountKinds} canDiscount={canDiscount} canAuthorizeDiscount={canAuthorizeDiscount} cancellationReasons={cancellationReasons} reopenReasons={reopenReasons} timeZone={timeZone}/>) : null}
      </div>)}
      </div>
    </MainContainer>)
}
const TABLE_EVENT_ICON: Record<string, typeof UserPlus> = {
    release: LogOut,
    occupy: Users,
    reserve: CalendarClock,
    disable: Ban,
    invite: QrCode,
}
function TableWorkspaceHeader({ table, tables, summaryIsOpen, onOpenSummary }: {
    table: TableContext
    tables: TableSwitcherItem[]
    summaryIsOpen: boolean
    onOpenSummary: () => void
}) {
    return (<header className="flex items-center gap-4">
      <Button type="button" variant="outline" size="sm" onClick={() => router.visit("/tables", { preserveScroll: true })} className="shrink-0 gap-1.5">
        <ChevronLeft className="size-3.5"/>{t("ui.react.features.staff.table_detail_workspace.tables")}
      </Button>

      <div className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:thin]" data-testid="table-switcher">
        <div className="flex gap-0.5 border-b" role="group" aria-label={t("ui.react.features.staff.table_detail_workspace.tables")}>
          {tables.map((item) => {
                const isActive = item.id === table.id
                return (<button key={item.id} type="button" onClick={() => {
                        if (isActive)
                            return
                        router.visit(`/tables/${item.externalId}`, { preserveScroll: false })
                    }} className={cn("-mb-px flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors md:min-h-0", isActive
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    // A red icon on a grey tab was too quiet to pull a waiter's eye
                    // across a busy floor. The whole tab goes destructive and pulses,
                    // so the table announces itself from the switcher rail.
                    item.alert && cn("border-destructive/60 bg-destructive/5 text-destructive hover:text-destructive", ALERT_PULSE_CLASS))} title={`${item.name} · ${t(operationalStatusLabelKey(item.operationalStatus))}`} data-testid={`table-switcher-item-${item.id}`} data-alert={item.alert ? "true" : undefined} aria-current={isActive ? "page" : undefined}>
                  <span className={cn("size-1.5 shrink-0 rounded-full", item.alert ? "bg-destructive" : operationalStatusDot(item.operationalStatus))}/>
                  <span>{item.name}</span>
                  {item.alert ? (<AlertTriangle className="size-3 shrink-0" aria-label={t("ui.react.features.staff.tables_overview.requires_attention")}/>) : null}
                </button>)
            })}
        </div>
      </div>

      {/* Phones only. From `lg` up the context rail is on screen and already
          states the status, the attention flag, the diners and every quick
          action this drawer holds — a second control for the same reading is
          just another thing to keep true. Below `lg` the rail is hidden, and
          this is the ONLY way to reach the diners, the invite QR and Liberar /
          Ocupar / Reservar / Deshabilitar, so it stays. */}
      <Button type="button" variant="outline" size="sm" onClick={onOpenSummary} className="shrink-0 gap-2 lg:hidden" title={t("ui.react.features.staff.table_detail_workspace.status_table")} aria-haspopup="dialog" aria-expanded={summaryIsOpen} data-testid="table-detail-summary-trigger">
        <span className={cn("size-2 shrink-0 rounded-full", operationalStatusDot(table.operationalStatus))}/>
        {t(operationalStatusLabelKey(table.operationalStatus))}
        {table.alert ? (<AlertTriangle className={cn("size-3.5 shrink-0 text-destructive", ALERT_PULSE_CLASS)} aria-label={t("ui.react.features.staff.tables_overview.requires_attention")} data-testid="table-detail-header-alert"/>) : null}
      </Button>
    </header>)
}
/**
 * Who is at this table, what it can do, and who is waiting at the door — on
 * screen for the whole shift rather than one tap deep.
 *
 * This used to live only in the summary drawer, and the drawer's own comment
 * admitted the cost: the header button was "the only reading of the table the
 * waiter gets for free". That was survivable for seats and metrics. It was not
 * survivable for `entryRequests` — somebody standing at the table waiting to be
 * let in — which the dashboard ranks as the single most urgent thing on the
 * floor. A screen cannot bury what the screen before it shouted about.
 *
 * The drawer stays for phones, where a 244px column would take the space the
 * work needs. Desktop reads this instead.
 */
function TableContextRail({ table, workspace, actions, onAction, isMutating, dynamicInvitesOn, menuIsAway, onReopenMenu, }: {
    table: TableContext
    workspace: TableAccountWorkspacePayload
    actions: TableDetailAction[]
    onAction: (action: TableDetailAction) => void
    isMutating: boolean
    dynamicInvitesOn: boolean
    /** True while the table is settling up and the menu has stepped back. */
    menuIsAway: boolean
    onReopenMenu: () => void
}) {
    const summary = tableDetailSummary(table, workspace)
    const showsDinersList = tableIsOccupied(table.operationalStatus)

    return (<aside className="hidden min-h-0 lg:block" data-testid="table-detail-context-rail">
      <Card density="compact" className="flex h-full min-h-0 gap-0 overflow-hidden">
        <div className="border-b px-4 pb-3">
          <p className="text-lg font-semibold leading-tight tracking-tight">{table.name}</p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className={cn("size-2 shrink-0 rounded-full", operationalStatusDot(table.operationalStatus))}/>
            <span className="text-sm font-medium">{t(operationalStatusLabelKey(table.operationalStatus))}</span>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            <SummaryMetric label={t("ui.react.features.staff.table_detail_workspace.seats")} value={summary.seats}/>
            <SummaryMetric label={t("ui.react.features.staff.table_detail_workspace.account")} value={summary.billCount}/>
          </dl>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <EntryRequestPanel requests={table.entryRequests} testId="table-detail-rail-entry-requests"/>

          {showsDinersList ? (<TableDinersList diners={table.diners} dynamicInvitesOn={dynamicInvitesOn} testIdPrefix="table-detail-rail"/>) : null}

          {dynamicInvitesOn && table.activeInvite ? (<div className="space-y-3 rounded-lg border bg-muted/20 p-3" data-testid="table-detail-rail-invite">
              <div className="rounded-md border bg-white p-2">
                <QRCode value={table.activeInvite.url} size={160} className="h-auto w-full"/>
              </div>
              <p className="text-sm font-medium">{t("ui.react.features.staff.table_detail_workspace.invite_link")}</p>
              <p className="break-all text-xs text-muted-foreground">{table.activeInvite.url}</p>
            </div>) : null}
        </div>

        <div className="space-y-2 border-t px-4 pt-3">
          {menuIsAway ? (<Button type="button" variant="outline" className="w-full" onClick={onReopenMenu} data-testid="table-detail-reopen-menu">
            <Plus className="size-4"/>{t("ui.react.features.staff.table_detail_workspace.add_something_else")}
          </Button>) : null}

          {actions.length > 0 ? (<div className="grid grid-cols-2 gap-2">
              {actions.map((action) => {
                  const Icon = TABLE_EVENT_ICON[action.value] ?? MoreHorizontal
                  const isDanger = action.value === "release" || action.value === "disable"
                  return (<Button key={action.value} type="button" variant="outline" size="quick-action" disabled={action.disabled || isMutating} onClick={() => onAction(action)} className={cn("size-auto min-h-14 w-full", isDanger && "text-destructive hover:bg-destructive/10 hover:text-destructive")} data-testid={`table-detail-rail-action-${action.value}`}>
                    <Icon className="size-4"/>
                    {tableActionLabel(action)}
                  </Button>)
              })}
            </div>) : null}
        </div>
      </Card>
    </aside>)
}
// Opened from the header rather than parked in a 300px column: the waiter needs
// the menu and the account on screen far more often than the metrics and the
// invite QR. Actions run without closing it — `invite` renders its QR right
// here, and closing on every tap would hide the result of the tap.
//
// The attention banner used to live here, which meant the one thing that must
// interrupt the waiter was hidden behind a tap. It now sits on the account card,
// on screen the whole shift; TableOrderPanel owns it.
function TableSummaryDrawer({ table, workspace, actions, onAction, isMutating, open, onOpenChange, dynamicInvitesOn, }: {
    table: TableContext
    workspace: TableAccountWorkspacePayload
    actions: TableDetailAction[]
    onAction: (action: TableDetailAction) => void
    isMutating: boolean
    open: boolean
    onOpenChange: (open: boolean) => void
    dynamicInvitesOn: boolean
}) {
    const summary = tableDetailSummary(table, workspace)
    const showsDinersCount = table.operationalStatus !== "free" && table.operationalStatus !== "disabled"
    // Out of service the unoccupied panel behind this drawer already owns the
    // "nobody is here yet, open it or send the QR" message; a second empty
    // roster would just say it again, quieter.
    //
    // Settling up is the other empty-by-design case: closing the bill ends every
    // visit (Location::BillClosureService), so a table being paid or waiting to
    // be cleared reports zero diners as a matter of course. Offering the invite
    // QR there tells the waiter to seat a party at a table they are clearing —
    // the count above is enough. Names still render if anyone is left.
    const settlingUp = table.operationalStatus === "paying" || table.operationalStatus === "awaiting_release"
    const showsDinersList = tableIsOccupied(table.operationalStatus) && (table.diners.length > 0 || !settlingUp)
    return (<ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="table-detail-summary-card">
        <DrawerHeader className="flex-row items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <DrawerTitle className="text-base">{t("ui.react.features.staff.table_detail_workspace.status_table")}</DrawerTitle>
            <DrawerDescription className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <StatusDotLabel status={table.operationalStatus} label={t(operationalStatusLabelKey(table.operationalStatus))}/>
              {showsDinersCount ? (<>
                <span aria-hidden="true">·</span>
                <span data-testid="table-detail-diners">
                  {table.activeDinersCount > 0
                    ? t("ui.react.features.staff.tables_overview.diners_count", { count: table.activeDinersCount })
                    : t("ui.react.features.staff.tables_overview.no_diners")}
                </span>
              </>) : null}
            </DrawerDescription>
          </div>
          <DrawerClose asChild>
            <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={t("ui.common.actions.close")}>
              <X className="size-4"/>
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {actions.length > 0 ? (<div className="flex flex-wrap gap-2">
              {actions.map((action) => {
                  const Icon = TABLE_EVENT_ICON[action.value] ?? MoreHorizontal
                  const isDanger = action.value === "release" || action.value === "disable"
                  return (<Button key={action.value} type="button" variant="outline" size="quick-action" disabled={action.disabled || isMutating} onClick={() => onAction(action)} className={cn(isDanger && "text-destructive hover:bg-destructive/10 hover:text-destructive")} data-testid={`table-detail-action-${action.value}`}>
                    <Icon className="size-5"/>
                    {tableActionLabel(action)}
                  </Button>)
              })}
            </div>) : null}

          <Separator/>

          <dl className="grid grid-cols-2 gap-3">
            <SummaryMetric label={t("ui.react.features.staff.table_detail_workspace.seats")} value={summary.seats}/>
            <SummaryMetric label={t("ui.react.features.staff.table_detail_workspace.orders")} value={summary.orderCount}/>
            <SummaryMetric label={t("ui.react.features.staff.table_detail_workspace.account")} value={summary.billCount}/>
            {dynamicInvitesOn && (<SummaryMetric label="QR" value={summary.hasActiveInvite ? table.activeInvite?.token ?? "—" : "—"} compact/>)}
          </dl>

          {showsDinersList ? (<>
            <Separator/>
            <TableDinersList diners={table.diners} dynamicInvitesOn={dynamicInvitesOn}/>
          </>) : null}

          <EntryRequestPanel requests={table.entryRequests}/>

          {dynamicInvitesOn && table.activeInvite ? (<div className="space-y-3 rounded-lg border bg-muted/20 p-3" data-testid="table-detail-invite-panel">
              <div className="rounded-md border bg-white p-2">
                <QRCode value={table.activeInvite.url} size={160} className="h-auto w-full"/>
              </div>
              <div className="min-w-0">
                {/* Said "Comensales" until the real diner list landed above it —
                    the QR is the way to invite them, not a roster of who is here. */}
                <p className="text-sm font-medium">{t("ui.react.features.staff.table_detail_workspace.invite_link")}</p>
                <p className="break-all text-xs text-muted-foreground" data-testid="table-detail-invite-url">{table.activeInvite.url}</p>
              </div>
              <Button asChild size="sm" variant="outline" className="w-full">
                <a href={table.activeInvite.url} target="_blank" rel="noreferrer">
                  <QrCode className="size-3.5"/>{t("ui.navigation.open_menu")}
                </a>
              </Button>
            </div>) : null}
        </div>
      </DrawerContent>
    </ResponsiveDrawer>)
}
// Who is sitting at the table, not just how many. The drawer's own count answers
// "is anyone here"; this answers "which of them is asking me for the check".
// `testIdPrefix` exists because this roster now renders twice — once in the
// desktop context rail, once in the drawer the phone still opens. Both are the
// same reading at different breakpoints, so they must not answer to the same
// selector: a Playwright query for one would resolve to two nodes.
function TableDinersList({ diners, dynamicInvitesOn, testIdPrefix = "table-detail" }: {
    diners: TableDiner[]
    dynamicInvitesOn: boolean
    testIdPrefix?: string
}) {
    return (<div className="space-y-2" data-testid={`${testIdPrefix}-diners-list`}>
      <p className="text-sm font-medium">{t("ui.react.features.staff.table_detail_workspace.diners")}</p>
      {diners.length > 0 ? (<ul className="space-y-2">
          {diners.map((diner) => (<li key={diner.id} className="flex items-center gap-2" data-testid={`${testIdPrefix}-diner-${diner.id}`}>
              <Avatar size="sm">
                <AvatarFallback>{diner.initials}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate text-sm">{diner.name}</span>
            </li>))}
        </ul>) : (<p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-diners-empty`}>
          {/* One sentence with a placeholder, not three fragments glued together:
              where the action name sits in it is the translator's call, not the
              component's. The name itself comes from the quick action's own key
              so renaming the button carries the hint with it.

              Without a QR the waiter can generate, the invite-flavoured hint would
              point at a control that is not on screen — the printed one is the
              door now, so the fallback says nothing button-specific at all. */}
          {dynamicInvitesOn
            ? t("ui.react.features.staff.table_detail_workspace.no_linked_diners", {
                invite: t("ui.react.features.staff.table_detail_workspace.invite"),
            })
            : t("ui.react.features.staff.table_detail_workspace.no_linked_diners_static")}
        </p>)}
    </div>)
}
function unoccupiedCopy(status: OperationalStatus, hasOutstandingBills: boolean) {
    if (status === "disabled") {
        return {
            title: t("ui.react.features.staff.table_detail_workspace.unoccupied_disabled_title"),
            // `release` is guarded by `settlement_pending_bills.none?`, and that
            // scope counts closed bills too — so while one is unpaid the Liberar
            // button in the summary drawer is disabled. Pointing at it anyway
            // would send the waiter to a dead control.
            hint: hasOutstandingBills
                ? t("ui.react.features.staff.table_detail_workspace.unoccupied_disabled_pending_hint")
                : t("ui.react.features.staff.table_detail_workspace.unoccupied_disabled_hint"),
        }
    }
    if (status === "reserved") {
        return {
            title: t("ui.react.features.staff.table_detail_workspace.unoccupied_reserved_title"),
            hint: t("ui.react.features.staff.table_detail_workspace.unoccupied_reserved_hint"),
        }
    }
    return {
        title: t("ui.react.features.staff.table_detail_workspace.unoccupied_free_title"),
        hint: t("ui.react.features.staff.table_detail_workspace.unoccupied_free_hint"),
    }
}
// Stands in for the menu while the table is out of service. A disabled table
// gets no call to action of its own on purpose: `occupy` only transitions from
// available/reserved, and the way back — `release`, which does accept
// `disabled` — is already a button in the summary drawer, so duplicating it
// here would just be a second name for the same thing. The hint names the
// drawer, since that button is now a tap away rather than on screen.
/**
 * How soon a booking has to be before it outranks a walk-in.
 *
 * Seating a reservation and opening the table are the same physical act, so the
 * two controls must not both read as primary. A booking still an hour out does
 * not stop a short turn before it; one arriving now does — and the copy has to
 * say the consequence rather than restate what the strip already shows.
 */
const RESERVATION_IMMINENT_MINUTES = 30

export function reservationIsImminent(reservation: TableReservation | null, now: number = Date.now()): boolean {
    if (!reservation) return false

    const startsAt = new Date(reservation.startsAt).getTime()
    if (Number.isNaN(startsAt)) return false

    // Already due counts: a party that is late is still the party this table is
    // being held for.
    return startsAt - now <= RESERVATION_IMMINENT_MINUTES * 60_000
}

function TableReservationStrip({ reservation, imminent, onSeat, isMutating, }: {
    reservation: TableReservation
    imminent: boolean
    onSeat: () => void
    isMutating: boolean
}) {
    return (<div className={cn("flex items-center gap-3 rounded-lg border p-3", imminent
            ? "border-status-reserved/45 bg-status-reserved/[0.08]"
            : "border-border bg-card")} data-testid="table-detail-reservation-strip">
      <span className="size-2 shrink-0 rounded-full bg-status-reserved"/>
      <div className="min-w-0 flex-1 text-left">
        <p className="text-sm font-semibold tabular-nums">
          {formatReservationTime(reservation.startsAt)}
          {" · "}
          {reservation.guestName}
          {" · "}
          {t("ui.react.features.staff.table_detail_workspace.reservation_party", { count: reservation.partySize })}
        </p>
        <p className={cn("mt-0.5 text-xs", imminent ? "font-medium text-status-reserved" : "text-muted-foreground")}>
          {imminent
            ? t("ui.react.features.staff.table_detail_workspace.reservation_arriving")
            : t("ui.react.features.staff.table_detail_workspace.reservation_booked")}
          {reservation.guestPhone ? ` · ${reservation.guestPhone}` : ""}
        </p>
      </div>
      <Button type="button" variant={imminent ? "default" : "outline"} size="sm" className="shrink-0" disabled={isMutating} onClick={onSeat} data-testid="table-detail-seat-reservation">
        {t("ui.react.features.staff.table_detail_workspace.reservation_seat")}
      </Button>
    </div>)
}

function formatReservationTime(iso: string) {
    return new Date(iso).toLocaleTimeString(intlLocale(), { hour: "2-digit", minute: "2-digit" })
}

function TableUnoccupiedPanel({ status, actions, onAction, isMutating, hasOutstandingBills, dynamicInvitesOn, reservation, onSeatReservation, }: {
    status: OperationalStatus
    actions: TableDetailAction[]
    onAction: (action: TableDetailAction) => void
    isMutating: boolean
    hasOutstandingBills: boolean
    dynamicInvitesOn: boolean
    reservation: TableReservation | null
    onSeatReservation: (reservation: TableReservation) => void
}) {
    const { title, hint } = unoccupiedCopy(status, hasOutstandingBills)
    const occupyAction = actions.find((action) => action.value === "occupy") ?? null
    const inviteAction = actions.find((action) => action.value === "invite") ?? null
    const showActions = status !== "disabled"
    // A disabled table is not being held for anybody: `occupy` refuses it, so
    // offering to seat a party there would be a control that cannot fire.
    const showsReservation = reservation !== null && status !== "disabled"
    const imminent = showsReservation && reservationIsImminent(reservation)
    return (<Card density="compact" className="h-full min-w-0 items-center justify-center overflow-y-auto" data-testid="table-detail-unoccupied-panel">
      <CardContent className="flex w-full max-w-lg flex-col items-center py-10 text-center">
        {showsReservation ? (<div className="mb-6 w-full">
          <TableReservationStrip reservation={reservation} imminent={imminent} isMutating={isMutating} onSeat={() => onSeatReservation(reservation)}/>
        </div>) : null}

        <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {status === "disabled" ? <Ban className="size-5"/> : <Users className="size-5"/>}
        </div>
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{hint}</p>

        {showActions ? (<>
            <div className="mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              {/* Steps down to outline while the booking is imminent: the party
                  this table is being held for is about to walk in, so opening it
                  for somebody else is the exception, not the default. */}
              <Button type="button" variant={imminent ? "outline" : "default"} className="h-11 gap-2 sm:h-10" disabled={!occupyAction || occupyAction.disabled || isMutating} onClick={() => occupyAction && onAction(occupyAction)} data-testid="table-detail-unoccupied-occupy">
                <Users className="size-4"/>
                {imminent
                  ? t("ui.react.features.staff.table_detail_workspace.open_without_reservation")
                  : t("ui.react.features.staff.tables_overview.open_table")}
              </Button>
              {dynamicInvitesOn && (<Button type="button" variant="outline" className="h-11 gap-2 sm:h-10" disabled={!inviteAction || inviteAction.disabled || isMutating} onClick={() => inviteAction && onAction(inviteAction)} data-testid="table-detail-unoccupied-invite">
                <QrCode className="size-4"/>
                {t("ui.react.features.staff.table_detail_workspace.unoccupied_invite")}
              </Button>)}
            </div>
            {/* The two buttons do NOT land in the same place, and this note used
                to claim they did. `occupy` runs `ensure_open_bill`; the QR one
                posts to Staff::Location::InvitesController, which only mints the
                invite. The bill is born later, when the diner opens the link
                (Location::Invite#register_visit! → active_bill_for_table) — on
                purpose, since opening it with the QR would leave orphan bills on
                tables nobody ever sat at. The names come from the buttons' own
                keys so renaming one carries the note with it.

                Only makes sense next to the button it explains — with dynamic
                invites off there is only one button, and it needs no footnote. */}
            {/* States the consequence rather than repeating the strip. "This
                table is reserved" is something the waiter can already see; what
                happens to the party if they open it anyway is not. */}
            {imminent && reservation ? (<p className="mt-3 max-w-sm text-xs text-foreground" data-testid="table-detail-reservation-warning">
              {t("ui.react.features.staff.table_detail_workspace.reservation_conflict_warning", {
                    time: formatReservationTime(reservation.startsAt),
                    guest: reservation.guestName,
                })}
            </p>) : null}
            {dynamicInvitesOn && (<p className="mt-3 max-w-sm text-xs text-muted-foreground" data-testid="table-detail-unoccupied-note">
              {t("ui.react.features.staff.table_detail_workspace.unoccupied_actions_note", {
                    open: t("ui.react.features.staff.tables_overview.open_table"),
                    invite: t("ui.react.features.staff.table_detail_workspace.unoccupied_invite"),
                })}
            </p>)}
          </>) : null}
      </CardContent>
    </Card>)
}
function SummaryMetric({ label, value, compact = false, }: {
    label: string
    value: string | number
    compact?: boolean
}) {
    return (<div className="space-y-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("font-semibold text-foreground", compact ? "truncate text-sm" : "text-xl tabular-nums tracking-tight")}>{value}</dd>
    </div>)
}
export function TableMenuBrowser({ categories, orphanItems, allCategories, query, onQueryChange, categoryFilter, onCategoryFilterChange, cartCounts, onItemClick, totalItems, }: {
    categories: MenuGridCategory[]
    orphanItems: MenuGridItem[]
    allCategories: MenuGridCategory[]
    query: string
    onQueryChange: (value: string) => void
    categoryFilter: string
    onCategoryFilterChange: (value: string) => void
    cartCounts: Record<number, number>
    onItemClick?: (item: MenuGridItem) => void
    totalItems: number
}) {
    return (<Card density="compact" className="h-full min-w-0 gap-0 overflow-hidden" data-testid="table-detail-menu-card">
      <CardHeader className="flex flex-col items-stretch gap-3 border-b pb-0">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Label htmlFor="table-menu-search" className="sr-only">{t("ui.react.features.staff.table_detail_workspace.search_dish_drink_ingredient")}</Label>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"/>
            <Input id="table-menu-search" value={query} onChange={(event) => onQueryChange(event.target.value)} type="search" placeholder={t("ui.react.features.staff.table_detail_workspace.search_dish_drink_ingredient")} className="h-10 pl-9"/>
          </div>

        </div>

        <div className="-mx-4 overflow-x-auto px-4">
          <div className="relative flex gap-0.5 border-b" role="group" aria-label={t("ui.react.features.staff.table_detail_workspace.menu_categories")}>
            <CategoryTab active={categoryFilter === "all"} onClick={() => onCategoryFilterChange("all")} label={t("ui.react.features.staff.table_detail_workspace.all")} count={allCategories.reduce((sum, category) => sum + category.items.length, 0)}/>
            {allCategories.map((category) => (<CategoryTab key={category.id} active={categoryFilter === String(category.id)} onClick={() => onCategoryFilterChange(String(category.id))} label={category.name} count={category.items.length}/>))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 overflow-y-auto pb-8 pt-4">
        {totalItems === 0 ? (<div className="mt-12 flex flex-col items-center gap-2 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Search className="size-5"/>
            </div>
            <p className="text-sm font-semibold">{t("ui.react.features.staff.table_detail_workspace.results")}</p>
            <p className="text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.adjust_search_select_other_category")}</p>
          </div>) : (<div className="space-y-7">
            {categories.map((category) => (<MenuSection key={category.id} title={category.name} subtitle={t(`ui.react.features.staff.table_detail_workspace.${category.items.length === 1 ? "dish_count_one" : "dish_count_other"}`, { count: category.items.length })}>
                <div className="overflow-hidden rounded-lg border">
                  {category.items.map((item) => (<DishRow key={item.id} item={item} cartCount={cartCounts[item.id] ?? 0} onItemClick={onItemClick}/>))}
                </div>
              </MenuSection>))}

            {orphanItems.length > 0 ? (<MenuSection title={t("ui.react.features.staff.table_detail_workspace.category")} subtitle={t(`ui.react.features.staff.table_detail_workspace.${orphanItems.length === 1 ? "dish_count_one" : "dish_count_other"}`, { count: orphanItems.length })}>
                <div className="overflow-hidden rounded-lg border">
                  {orphanItems.map((item) => (<DishRow key={item.id} item={item} cartCount={cartCounts[item.id] ?? 0} onItemClick={onItemClick}/>))}
                </div>
              </MenuSection>) : null}
          </div>)}
      </CardContent>
    </Card>)
}
function CategoryTab({ active, onClick, label, count, }: {
    active: boolean
    onClick: () => void
    label: string
    count: number
}) {
    return (<button type="button" aria-pressed={active} onClick={onClick} className={cn("-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors", active
            ? "border-primary text-foreground"
            : "border-transparent text-muted-foreground hover:text-foreground")}>
      {label}
      <span className="text-xs tabular-nums text-muted-foreground">{count}</span>
    </button>)
}
function MenuSection({ title, subtitle, children, }: {
    title: string
    subtitle: string
    children: React.ReactNode
}) {
    return (<section>
      <div className="mb-3 flex items-baseline gap-2">
        <h3 className="text-base font-semibold tracking-tight">{title}</h3>
        <span className="text-xs text-muted-foreground">{subtitle}</span>
      </div>
      {children}
    </section>)
}
function DishRow({ item, cartCount, onItemClick, }: {
    item: MenuGridItem
    cartCount: number
    onItemClick?: (item: MenuGridItem) => void
}) {
    const isSoldOut = item.status === "sold_out"
    const isUnavailable = item.available === false
    const isBlocked = isSoldOut || isUnavailable
    const isClickable = typeof onItemClick === "function" && !isBlocked
    // No thumbnail, by design. A dish here is a name, a description and a price;
    // the picture answers a question the diner asks and the waiter does not, and
    // dropping it fits half again as many dishes on screen before scrolling.
    return (<button type="button" onClick={isClickable ? () => onItemClick(item) : undefined} disabled={!isClickable} className={cn("flex w-full items-center gap-3 border-b bg-card px-3 py-2.5 text-left transition-colors last:border-b-0", isClickable && "hover:bg-muted/40", isBlocked && "cursor-not-allowed opacity-60")} data-testid={`table-detail-dish-${item.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold tracking-tight">
          {item.name}
          {cartCount > 0 ? (<span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-brand-ink">
              ×{cartCount}
            </span>) : null}
        </p>
        {item.description ? (<p className="truncate text-xs text-muted-foreground">{item.description}</p>) : null}
      </div>
      <p className="ml-auto shrink-0 text-xs font-semibold tabular-nums">
        {formatPrice(item.price, item.currency)}
      </p>
      {/* A greyed-out row that just doesn't respond leaves the waiter guessing:
          say which block it is, the same way DishCard's badge does. */}
      {isBlocked ? (<span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {isSoldOut
            ? t("ui.react.features.staff.table_detail_workspace.soldout")
            : t("ui.react.features.staff.table_detail_workspace.unavailable")}
        </span>) : null}
      {isClickable ? (<span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Plus className="size-3.5"/>
        </span>) : null}
    </button>)
}
export function TableOrderPanel({ tab, onTabChange, closedBills, activeBill, openBills, activeOrders, amount, currency, alertReasons, onAcknowledgeWaiterCall, tableIsMutating, resourceIsLoading, onCloseBill, onPay, onResolvePayment, refundReasons, canRefund, discountReasons, discountKinds, canDiscount, canAuthorizeDiscount, cancellationReasons, reopenReasons, timeZone, }: {
    tab: "active" | "history"
    onTabChange: (value: "active" | "history") => void
    closedBills: TableAccountBill[]
    activeBill: TableAccountBill | null
    /** Why the table is asking for a human. Empty means it is quiet. */
    alertReasons: AlertReason[]
    onAcknowledgeWaiterCall: () => void
    tableIsMutating: boolean
    // The whole list, not a count. Nothing can open a second account on a table
    // any more, but rows that already have one predate the invariant and every
    // one of them holds real money. `activeBill` is openBills[0], so anything
    // past the first renders in no tab — the reconciliation block below is the
    // only place they are reachable, and they are settled here rather than by
    // sending staff to another screen mid-service.
    openBills: TableAccountBill[]
    activeOrders: TableAccountOrder[]
    amount: number
    currency: string
    resourceIsLoading: (resourceKey: string) => boolean
    onCloseBill: (billId: number) => void
    onPay: (billId: number, method: "cash" | "physical_card_terminal", orderIds?: number[]) => void
    onResolvePayment: (attemptId: number, status: "succeeded" | "failed") => void
    refundReasons: RefundReasonOption[]
    canRefund: boolean
    discountReasons: DiscountReasonOption[]
    discountKinds: DiscountKindOption[]
    canDiscount: boolean
    canAuthorizeDiscount: boolean
    /** The closed cancellation catalog, straight from `Location::CancellationPolicy.options`. */
    cancellationReasons: CancellationReasonOption[]
    reopenReasons: ReopenReasonOption[]
    timeZone?: string
}) {
    const alertMessage = alertMessageFor(alertReasons)
    const waiterRequested = alertReasons.includes("waiter_requested")
    // Both halves, and the server owns both: the page said this person's role
    // and the location's feature flag allow a discount at all; the bill says
    // whether its own state still does. A bill from a serializer that predates
    // discounts sends neither, and falls back to the page-level answer.
    const billAllowsDiscount = canDiscount && (activeBill?.allowedActions.canApplyDiscount ?? true)
    const billAllowsAuthorization = canAuthorizeDiscount && (activeBill?.allowedActions.canAuthorizeDiscount ?? true)
    const pendingDiscounts = activeBill?.pendingDiscounts ?? []
    const billDiscountTotal = Number(activeBill?.discountTotal ?? 0)
    // Which lines the waiter has ticked for a partial charge. Local to the panel
    // and never lifted: it is a scratch pad for the next charge, not state the
    // server or any other screen has an opinion about.
    const [pickedOrderIds, setPickedOrderIds] = useState<ReadonlySet<number>>(() => new Set())
    const activeBillId = activeBill?.id ?? null
    // A different account is a different scratch pad. Ids are per-order and
    // never collide across bills, but carrying a tick from one to the next would
    // still be a selection the waiter did not make.
    useEffect(() => {
        setPickedOrderIds(new Set())
    }, [activeBillId])
    const selectedOrderIds = useMemo(() => reconcileSelection(pickedOrderIds, activeOrders), [pickedOrderIds, activeOrders])
    const toggleOrderSelection = (orderId: number) => {
        setPickedOrderIds((current) => {
            const next = new Set(current)
            if (!next.delete(orderId))
                next.add(orderId)
            return next
        })
    }
    // Nothing ticked charges everything still owed — the button this screen has
    // always had. `amount` is already the outstanding figure, not the ticket.
    const chargeAmount = selectedOrderIds.length > 0 ? selectionSubtotal(activeOrders, selectedOrderIds) : amount
    const paidTotal = paymentDisplayAmount(activeBill?.paidTotal ?? 0)
    // The bill-level control asks the same two server answers the per-line one
    // gets from `discountEligibility`: may this person discount, and does their
    // discount need a manager. `requiresAuthorization` is a fact about the
    // person, not about the money, which is why it can be read off the role flag
    // without the browser deciding anything. The ceiling is left at zero and the
    // dialog simply omits it unless the serializer sends a real bill-level
    // eligibility — a figure nobody computed is not a figure to show.
    const billEligibility: DiscountEligibility | null = activeBill?.discountEligibility ??
        (activeBill && billAllowsDiscount
            ? { allowed: true, maxDiscountableCents: 0, reasonCode: null, requiresAuthorization: !billAllowsAuthorization }
            : null)
    return (<aside className="h-full min-h-0">
      <Card density="compact" className="flex h-full min-h-0 gap-0 overflow-hidden" data-testid="table-detail-orders-card">
      <CardHeader className="gap-3 border-b">
          <CardTitle className="text-base">{t("ui.react.features.staff.table_detail_workspace.orders")}</CardTitle>
        {/* The account is the panel the waiter already has open, so a table asking
            for a human says so here rather than one tap deep in the summary drawer. */}
        {alertMessage ? (<div className="col-span-full flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs font-medium text-destructive" data-testid="table-detail-alert" role="status">
            <AlertTriangle className={cn("size-3.5 shrink-0", ALERT_PULSE_CLASS)}/>
            <span className="min-w-0 flex-1">{alertMessage}</span>
            {waiterRequested ? (<Button type="button" variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs" disabled={tableIsMutating} onClick={onAcknowledgeWaiterCall} data-testid="table-detail-acknowledge-waiter-call">
                {t("ui.react.features.staff.tables_overview.acknowledge_waiter_call")}
              </Button>) : null}
          </div>) : null}
        <div className="col-span-full flex gap-1 rounded-lg bg-muted p-0.5" role="group" aria-label={t("ui.react.features.staff.table_detail_workspace.bill_views")}>
          <button type="button" data-testid="table-detail-tab-active" aria-pressed={tab === "active"} onClick={() => onTabChange("active")} className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors", tab === "active"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground")}>{t("ui.react.features.staff.table_detail_workspace.open")}<span className={cn("rounded-full px-1.5 py-0.5 tabular-nums", tab === "active"
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground")}>
              {openBills.length}
            </span>
          </button>
          <button type="button" data-testid="table-detail-tab-history" aria-pressed={tab === "history"} onClick={() => onTabChange("history")} className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors", tab === "history"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground")}>{t("ui.react.features.staff.table_detail_workspace.history")}<span className={cn("rounded-full px-1.5 py-0.5 tabular-nums", tab === "history"
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground")}>
              {closedBills.length}
            </span>
          </button>
        </div>
      </CardHeader>

      {tab === "active" ? (<>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Legacy tables only: everything past openBills[0] is invisible in the
                normal layout and blocks Location::Table#ready_for_manual_release?,
                so the table can never be freed until these are settled here.

                Every branch below is reachable. canCancel is deliberately absent —
                Staff::BillSerializer gates it on bill.closed? and every bill here is
                open, so it could only ever render as nothing. The pending-attempt
                section is the one that must not be dropped: an attempt turns close
                and charge off at the same time, so without it pressing Cobrar is
                what leaves the card with no way forward. */}
            {openBills.length > 1 ? (<section className="mx-4 mt-3 space-y-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3" data-testid="table-detail-reconciliation">
                <p className="flex items-center gap-1.5 text-sm font-semibold">
                  <AlertTriangle className="size-4 shrink-0 text-destructive"/>
                  {t("ui.react.features.staff.table_detail_workspace.extra_open_bills")}
                </p>
                <p className="text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.extra_open_bills_hint")}</p>
                {openBills.slice(1).map((extra) => (<article key={extra.id} className="rounded-lg border bg-card p-3" data-testid={`table-detail-extra-bill-${extra.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{extra.label}</span>
                      <span className="text-sm font-semibold tabular-nums">{formatPrice(extra.total, extra.currency)}</span>
                    </div>
                    <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                      {extra.ordersCount} {t("ui.react.features.staff.table_detail_workspace.item")}{extra.ordersCount !== 1 ? "s" : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {extra.allowedActions.canCreatePaymentAttempt ? (<DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button type="button" size="sm" className="flex-1 gap-1.5" disabled={resourceIsLoading(staffMutationResourceKey("bill", extra.id))} data-testid={`table-detail-extra-pay-button-${extra.id}`}>
                              <CreditCard className="size-3.5"/>{t("ui.react.features.staff.table_detail_workspace.charge_2")}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onSelect={() => onPay(extra.id, "cash")}>{t("ui.react.features.staff.table_detail_workspace.charge_cash")}</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => onPay(extra.id, "physical_card_terminal")}>{t("ui.react.features.staff.table_detail_workspace.charge_terminal")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>) : null}
                      {extra.allowedActions.canClose ? (<Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => onCloseBill(extra.id)} disabled={resourceIsLoading(staffMutationResourceKey("bill", extra.id))} data-testid={`table-detail-extra-close-bill-${extra.id}`}>
                          {t("ui.react.features.staff.bill_detail_drawer.close_bill")}
                        </Button>) : null}
                    </div>

                    {extra.pendingPaymentAttempt && extra.allowedActions.canResolvePendingAttempt ? (<div className="mt-3 space-y-2 rounded-lg border p-3 text-sm" data-testid={`table-detail-extra-pending-attempt-${extra.id}`}>
                        <p className="flex items-center gap-1.5 font-medium">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-occupied"/>
                          {t("ui.react.features.staff.table_detail_workspace.payment_progress")}
                        </p>
                        <p className="text-muted-foreground">
                          {paymentMethodLabel(extra.pendingPaymentAttempt.paymentMethod)}{" "}{t("ui.react.features.staff.table_detail_workspace.text")}{" "}
                          <span className="font-medium tabular-nums text-foreground">{formatPrice(extra.pendingPaymentAttempt.totalAmount, extra.currency)}</span>
                        </p>
                        {extra.pendingPaymentAttempt.paymentMethod === "digital_in_app" ? (<p className="text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.digital_payment_auto_confirmed")}</p>) : (<div className="flex flex-wrap gap-2 pt-1">
                            <Button type="button" size="sm" variant="outline" disabled={resourceIsLoading(staffMutationResourceKey("attempt", extra.pendingPaymentAttempt.id))} onClick={() => onResolvePayment(extra.pendingPaymentAttempt!.id, "succeeded")} data-testid={`table-detail-extra-confirm-payment-${extra.id}`}>{t("ui.react.features.staff.table_detail_workspace.confirm_payment")}</Button>
                            <Button type="button" size="sm" variant="destructive" disabled={resourceIsLoading(staffMutationResourceKey("attempt", extra.pendingPaymentAttempt.id))} onClick={() => onResolvePayment(extra.pendingPaymentAttempt!.id, "failed")} data-testid={`table-detail-extra-mark-failure-${extra.id}`}>{t("ui.react.features.staff.table_detail_workspace.mark_failure")}</Button>
                          </div>)}
                      </div>) : null}
                  </article>))}
              </section>) : null}

            {activeBill ? (<>
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <span>{activeBill.label}</span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", billStatusDot(activeBill.status))}/>
                      {activeBill.statusLabel}
                    </span>
                  </div>
                </div>

                {/* What a waiter asked for and nobody has decided. It sits above
                    the lines, not beside one: a pending discount changes no
                    total yet, so putting it on the line would show a price the
                    diner is not being charged. */}
                {canDiscount ? (<DiscountPendingStrip discounts={pendingDiscounts} currency={currency} canAuthorize={billAllowsAuthorization}/>) : null}

                {/* One explanation per bill, not one per row: every refusal the
                    bill can report — a charge in flight, a split settling — is
                    true of every line at once. */}
                {canDiscount && activeBill.discountEligibility && !activeBill.discountEligibility.allowed ? (<DiscountBlockedNotice eligibility={activeBill.discountEligibility}/>) : null}

                {activeOrders.length === 0 ? (<div className="mx-4 mb-4 flex flex-col items-center rounded-xl border border-dashed p-8 text-center">
                    <div className="mb-2 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Utensils className="size-5"/>
                    </div>
                    <p className="text-sm font-semibold">{t("ui.react.features.staff.table_detail_workspace.start_bill")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.tap_dish_menu_add")}</p>
                  </div>) : (<>
                    <div className="flex items-center gap-2 border-y bg-muted/40 px-4 py-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.bill")}</span>
                      <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                        {activeOrders.length} {t("ui.react.features.staff.table_detail_workspace.item")}{activeOrders.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {activeOrders.map((order) => (<OrderLine key={order.id} order={order} billId={activeBill.id} canDiscount={billAllowsDiscount} discountReasons={discountReasons} discountKinds={discountKinds} selectable={activeBill.allowedActions.canCreatePaymentAttempt && lineIsSelectable(order)} selected={selectedOrderIds.includes(order.id)} onToggleSelection={toggleOrderSelection}/>))}
                  </>)}

                <BillPaymentsPanel bill={activeBill} currency={currency} resourceIsLoading={resourceIsLoading} onResolvePayment={onResolvePayment} refundReasons={refundReasons} canRefund={canRefund} timeZone={timeZone}/>
              </>) : (<div className="mx-4 my-6 flex flex-col items-center rounded-xl border border-dashed p-8 text-center">
                <Receipt className="mb-2 size-6 text-muted-foreground"/>
                <p className="text-sm font-semibold">{t("ui.react.features.staff.table_detail_workspace.no_active_bill")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.choose_item_menu_start")}</p>
              </div>)}
          </div>

          <div className="border-t bg-background p-4">
            <dl className="mb-3 space-y-1 text-sm">
              {/* Above the charge, because that is the order the reading goes in:
                  this much came off, and this is what is left to collect. The
                  amount below is already net of it — this line names the money,
                  it does not deduct it a second time. */}
              {billDiscountTotal > 0 ? (<div className="flex items-center justify-between text-muted-foreground">
                  <dt>{tDiscount("bill_discount_total")}</dt>
                  <dd className="tabular-nums" data-testid="bill-discount-total">
                    −{formatPrice(billDiscountTotal, currency)}
                  </dd>
                </div>) : null}
              {/* Only once something has been collected. On a bill nobody has
                  paid into, a "Pagado $0.00" line is a row that answers a
                  question the table has not asked yet. */}
              {paidTotal > 0 ? (<div className="flex items-center justify-between text-muted-foreground">
                  <dt>{t("ui.react.features.staff.table_detail_workspace.amount_paid")}</dt>
                  <dd className="tabular-nums" data-testid="bill-paid-total">
                    {formatPrice(paidTotal, currency)}
                  </dd>
                </div>) : null}
              <div className="flex items-center justify-between text-base font-semibold">
                <dt>{t(paidTotal > 0
            ? "ui.react.features.staff.table_detail_workspace.amount_outstanding"
            : "ui.react.features.staff.table_detail_workspace.amount_to_charge")}</dt>
                <dd className="text-lg tabular-nums" data-testid="bill-outstanding-total">{formatPrice(amount, currency)}</dd>
              </div>
              {/* The selection is its own figure, beside the bill's, never
                  instead of it: what the waiter is about to charge and what the
                  table still owes are two different questions and the screen has
                  to keep answering both. */}
              {selectedOrderIds.length > 0 ? (<div className="flex items-center justify-between text-muted-foreground">
                  <dt>{t("ui.react.features.staff.table_detail_workspace.selected_lines", { count: selectedOrderIds.length })}</dt>
                  <dd className="tabular-nums" data-testid="bill-selection-subtotal">
                    {formatPrice(chargeAmount, currency)}
                  </dd>
                </div>) : null}
            </dl>

            <div className="flex flex-col gap-2">
              {activeBill?.allowedActions.canCreatePaymentAttempt ? (<DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" className="h-10 w-full gap-2" disabled={!activeBill ||
                    activeOrders.length === 0 ||
                    resourceIsLoading(staffMutationResourceKey("bill", activeBill.id))} data-testid={`table-detail-pay-button-${activeBill.id}`}>
                      <CreditCard className="size-4"/>{t("ui.react.features.staff.table_detail_workspace.charge")}{" "}{formatPrice(chargeAmount, currency)}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onSelect={() => onPay(activeBill.id, "cash", selectedOrderIds)}>{t("ui.react.features.staff.table_detail_workspace.charge_cash")}</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => onPay(activeBill.id, "physical_card_terminal", selectedOrderIds)}>{t("ui.react.features.staff.table_detail_workspace.charge_terminal")}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>) : (<Button type="button" variant="outline" className="h-10 w-full gap-2" disabled>
                  <CreditCard className="size-4"/>{t("ui.react.features.staff.table_detail_workspace.charge_2")}</Button>)}

              <div className="flex gap-2">
                {/* One discount over the whole account, apportioned line by line
                    on the server. It sits beside Close rather than beside Charge:
                    it is a correction to what the bill says, not a way to collect. */}
                {/* `canDiscount` first, and not because `billEligibility` is
                    usually enough: it is the feature-and-role answer, and a
                    payload field must never be able to stand in for it. Gating on
                    the eligibility object alone once put this control on the
                    screen of a restaurant that does not have discounts, because
                    Location::DiscountEligibility answers about the BILL and knows
                    nothing about the feature flag. */}
                {canDiscount && activeBill && billEligibility && activeOrders.length > 0 ? (<DiscountAction billId={activeBill.id} scope="bill" eligibility={billEligibility} kinds={discountKinds} reasons={discountReasons} currency={currency}/>) : null}
                {activeBill?.allowedActions.canClose ? (<Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => onCloseBill(activeBill.id)} disabled={resourceIsLoading(staffMutationResourceKey("bill", activeBill.id))} data-testid={`table-detail-close-bill-${activeBill.id}`}>
                    {t("ui.react.features.staff.bill_detail_drawer.close_bill")}
                  </Button>) : null}
                {activeBill?.allowedActions.canCancel ? (<CancelReasonDialog path={CANCEL_PATHS.bill(activeBill.id)} payload={billCancelPayload} reasons={cancellationReasons} disabled={resourceIsLoading(staffMutationResourceKey("bill", activeBill.id))} triggerLabel={t("ui.common.actions.cancel")} triggerTestId={`table-detail-cancel-bill-${activeBill.id}`} triggerClassName="flex-1"/>) : null}
              </div>
            </div>
          </div>
        </>) : (<div className="min-h-0 flex-1 overflow-y-auto p-4">
          {closedBills.length === 0 ? (<div className="flex flex-col items-center rounded-xl border border-dashed p-8 text-center">
              <Receipt className="mb-2 size-6 text-muted-foreground"/>
              <p className="text-sm font-semibold">{t("ui.react.features.staff.table_detail_workspace.history_2")}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.closed_bills_placeholder")}</p>
            </div>) : (<div className="flex flex-col gap-3">
              {closedBills.map((bill) => (<article key={bill.id} className="rounded-xl border bg-card p-3 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      {bill.label}
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", billStatusDot(bill.status))}/>
                        {bill.statusLabel}
                      </span>
                    </div>
                    <p className="text-sm font-semibold tabular-nums">
                      {formatPrice(bill.total, bill.currency)}
                    </p>
                  </div>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                    {bill.ordersCount} {t("ui.react.features.staff.table_detail_workspace.item")}{bill.ordersCount !== 1 ? "s" : ""}
                  </p>
                  {/* Reopen used to be the only action here. It is also the first one
                      the server withholds — a table already carrying an open bill turns
                      canReopen off — so on its own it leaves a closed unpaid bill with no
                      way to collect. Charge and cancel are what keep the money reachable. */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {/* Reopening asks for a reason, like cancelling does. The
                        control report answers "who put this settled account back
                        in service, and why" — reopening after the drawer was
                        counted is the classic way to re-ring a table — and a row
                        with no reason would be the weakest line in it. */}
                    {bill.allowedActions.canReopen ? (<CancelReasonDialog path={REOPEN_PATHS.bill(bill.id)} payload={billReopenPayload} reasons={reopenReasons} disabled={resourceIsLoading(staffMutationResourceKey("bill", bill.id))} triggerLabel={t("ui.react.features.staff.table_detail_workspace.reopen")} triggerVariant="outline" triggerTestId={`table-detail-history-reopen-bill-${bill.id}`} triggerClassName="flex-1"/>) : null}
                    {bill.allowedActions.canCreatePaymentAttempt ? (<DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" size="sm" className="flex-1 gap-1.5" disabled={resourceIsLoading(staffMutationResourceKey("bill", bill.id))} data-testid={`table-detail-history-pay-button-${bill.id}`}>
                            <CreditCard className="size-3.5"/>{t("ui.react.features.staff.table_detail_workspace.charge_2")}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onSelect={() => onPay(bill.id, "cash")}>{t("ui.react.features.staff.table_detail_workspace.charge_cash")}</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => onPay(bill.id, "physical_card_terminal")}>{t("ui.react.features.staff.table_detail_workspace.charge_terminal")}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>) : null}
                    {bill.allowedActions.canCancel ? (<CancelReasonDialog path={CANCEL_PATHS.bill(bill.id)} payload={billCancelPayload} reasons={cancellationReasons} disabled={resourceIsLoading(staffMutationResourceKey("bill", bill.id))} triggerLabel={t("ui.common.actions.cancel")} triggerTestId={`table-detail-history-cancel-bill-${bill.id}`} triggerClassName="flex-1"/>) : null}
                  </div>

                  {/* A pending attempt switches every action above off at once. Render
                      nothing here and the card has no buttons left — pressing Charge
                      would be what stranded the money. This is the way back out. */}
                  {bill.pendingPaymentAttempt && bill.allowedActions.canResolvePendingAttempt ? (<section className="mt-3 space-y-2 rounded-lg border p-3 text-sm" data-testid={`table-detail-history-pending-attempt-${bill.id}`}>
                      <p className="flex items-center gap-1.5 font-medium">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-occupied"/>
                        {t("ui.react.features.staff.table_detail_workspace.payment_progress")}
                      </p>
                      <p className="text-muted-foreground">
                        {paymentMethodLabel(bill.pendingPaymentAttempt.paymentMethod)}{" "}{t("ui.react.features.staff.table_detail_workspace.text")}{" "}
                        <span className="font-medium tabular-nums text-foreground">{formatPrice(bill.pendingPaymentAttempt.totalAmount, bill.currency)}</span>
                      </p>
                      {bill.pendingPaymentAttempt.paymentMethod === "digital_in_app" ? (<p className="text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.digital_payment_auto_confirmed")}</p>) : (<div className="flex flex-wrap gap-2 pt-1">
                          <Button type="button" size="sm" variant="outline" disabled={resourceIsLoading(staffMutationResourceKey("attempt", bill.pendingPaymentAttempt.id))} onClick={() => onResolvePayment(bill.pendingPaymentAttempt!.id, "succeeded")} data-testid={`table-detail-history-confirm-payment-${bill.id}`}>{t("ui.react.features.staff.table_detail_workspace.confirm_payment")}</Button>
                          <Button type="button" size="sm" variant="destructive" disabled={resourceIsLoading(staffMutationResourceKey("attempt", bill.pendingPaymentAttempt.id))} onClick={() => onResolvePayment(bill.pendingPaymentAttempt!.id, "failed")} data-testid={`table-detail-history-mark-failure-${bill.id}`}>{t("ui.react.features.staff.table_detail_workspace.mark_failure")}</Button>
                        </div>)}
                    </section>) : null}

                  {/* Closed bills carry the refunds staff actually get asked for.
                      A diner who complains does it after paying — often the next
                      day — and until now the money they paid was only reachable
                      while the bill was still open, which is a window measured in
                      minutes. The same row renders here as on the open account. */}
                  {(bill.settledPayments ?? []).length > 0 ? (<section className="mt-3 space-y-2" data-testid={`table-detail-history-settled-${bill.id}`}>
                      <p className="text-xs font-semibold text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.payments_settled")}</p>
                      {bill.settledPayments.map((payment) => (<SettledPaymentRow key={payment.id} payment={payment} currency={bill.currency} refundReasons={refundReasons} canRefund={canRefund} timeZone={timeZone}/>))}
                    </section>) : null}
                </article>))}
            </div>)}
        </div>)}
      </Card>
    </aside>)
}
/**
 * One line of the ticket, and the money that came off it.
 *
 * The original price stays visible whenever a discount reduced the line —
 * `Location::DiscountPolicy` rule 3, and the reason it exists: after the fact
 * nobody can tell a cheap dish from a discounted one unless both figures are on
 * the ticket. `grossLineTotal` is struck through beside the charged
 * `lineTotal`, never instead of it.
 */
function OrderLine({ order, billId, canDiscount, discountReasons, discountKinds, selectable = false, selected = false, onToggleSelection }: {
    order: TableAccountOrder
    billId: number
    canDiscount: boolean
    discountReasons: DiscountReasonOption[]
    discountKinds: DiscountKindOption[]
    /** Whether this line can join the next partial charge. */
    selectable?: boolean
    selected?: boolean
    onToggleSelection?: (orderId: number) => void
}) {
    const reduced = discountedLine(order.discountAmount)
    const liveDiscount = order.discount ?? null
    const appliedDiscount = liveDiscount?.status === "applied" ? liveDiscount : null
    // A rule does not hold the line against a person. `index_one_live_discount_per_order`
    // still allows one live row, but a manual discount SUPERSEDES a system one
    // rather than being refused by it, so the trigger has to stay on a promoted
    // line — gating on `!liveDiscount` hid the discount action on every line a
    // promotion had reached, which is precisely the line a manager comps.
    const manualDiscount = systemDiscount(liveDiscount) ? null : liveDiscount
    const paymentStatus = order.paymentStatus ?? "unpaid"
    const settled = paymentStatus === "paid"
    return (<div className={cn("flex items-start gap-2.5 border-b px-4 py-2.5 text-sm last:border-b-0", settled && "opacity-60")} data-testid={`table-detail-order-line-${order.id}`}>
      {/* The pick control lives on the LEFT, before the quantity. The right side
          already holds the discount actions, and a checkbox among them would
          read as one more thing this row DOES rather than as what it is. */}
      {selectable ? (<Checkbox className="mt-0.5" checked={selected} onCheckedChange={() => onToggleSelection?.(order.id)} aria-label={t("ui.react.features.staff.table_detail_workspace.select_line_to_charge", { name: order.name })} data-testid={`table-detail-order-select-${order.id}`}/>) : null}
      <span className="min-w-7 rounded bg-muted px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums">
        ×{order.quantity}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">{order.name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {order.statusLabel} · {order.customerName}
        </p>
        {/* Only when the line has left `unpaid`. On a bill nobody has paid into,
            "Pendiente de pago" on every row is noise that says nothing — the
            badge earns its place by marking the exception. */}
        {paymentStatus !== "unpaid" ? (<StatusBadge className="mt-1" tone={paymentTone(paymentStatus)} label={order.paymentStatusLabel ?? paymentStatus} data-testid={`table-detail-order-payment-status-${order.id}`}/>) : null}
        {order.notes ? (<p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MessageSquare className="size-2.5"/> {order.notes}
          </p>) : null}
        {reduced && appliedDiscount ? (<p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`table-detail-order-discount-${order.id}`}>
            <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-warning"/>
            <span className="min-w-0 truncate">
              {tDiscount(systemDiscount(appliedDiscount) ? "line_promotion" : "line_discount")} · {discountSourceLabel(appliedDiscount)} · −{formatPrice(order.discountAmount ?? 0, order.currency)}
            </span>
          </p>) : null}
      </div>
      <div className="flex shrink-0 items-start gap-1">
        <p className="text-right text-sm font-semibold tabular-nums">
          {reduced && order.grossLineTotal ? (<span className="mr-1.5 font-normal text-muted-foreground line-through" title={tDiscount("original_price")}>
              {formatPrice(order.grossLineTotal, order.currency)}
            </span>) : null}
          {formatPrice(order.lineTotal, order.currency)}
        </p>
        {canDiscount && appliedDiscount ? (<DiscountVoidButton discountId={appliedDiscount.id}/>) : null}
        {canDiscount && !manualDiscount ? (<DiscountAction billId={billId} scope="line" orderId={order.id} eligibility={order.discountEligibility} kinds={discountKinds} reasons={discountReasons} currency={order.currency}/>) : null}
      </div>
    </div>)
}

/**
 * What the table owes, who is paying it, and who already did.
 *
 * A bill settles in parallel, so this cannot show "the" payment attempt any
 * more. In-flight rows carry their age because the useful question at a table
 * is which diner has been stuck longest, not how many are paying.
 */
function BillPaymentsPanel({ bill, currency, resourceIsLoading, onResolvePayment, refundReasons, canRefund, timeZone }: {
    bill: TableAccountBill
    currency: string
    resourceIsLoading: (key: string) => boolean
    onResolvePayment: (paymentAttemptId: number, status: "succeeded" | "failed") => void
    refundReasons: RefundReasonOption[]
    canRefund: boolean
    timeZone?: string
}) {
    const inFlight = bill.pendingPaymentAttempts ?? []
    const settled = bill.settledPayments ?? []
    const split = bill.split

    if (!split && inFlight.length === 0 && settled.length === 0) return null

    return (<section className="mx-4 my-3 space-y-3 rounded-lg border p-3 text-sm" data-testid={`bill-payments-${bill.id}`}>
        {split ? (<div className="space-y-1 rounded-md border border-brand/40 bg-brand/5 p-2.5" data-testid={`bill-split-${bill.id}`}>
            <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{t("ui.react.features.staff.table_detail_workspace.split_progress", { count: split.payerCount })}</span>
                <span className="text-xs tabular-nums text-muted-foreground">
                    {t("ui.react.features.staff.table_detail_workspace.split_outstanding")}{" "}
                    <span className="font-semibold text-foreground">{formatPrice(split.outstanding, currency)}</span>
                </span>
            </div>
        </div>) : null}

        {/* When nobody is mid-payment the useful view is what was already
            collected, not an empty list. */}
        <Tabs defaultValue={inFlight.length > 0 ? "in-flight" : "settled"}>
            <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="in-flight" data-testid={`bill-payments-tab-in-flight-${bill.id}`}>
                    {t("ui.react.features.staff.table_detail_workspace.payments_in_flight")}
                    {inFlight.length > 0 ? ` (${inFlight.length})` : ""}
                </TabsTrigger>
                <TabsTrigger value="settled" data-testid={`bill-payments-tab-settled-${bill.id}`}>
                    {t("ui.react.features.staff.table_detail_workspace.payments_settled")}
                    {settled.length > 0 ? ` (${settled.length})` : ""}
                </TabsTrigger>
            </TabsList>

            <TabsContent value="in-flight" className="mt-2 space-y-2">
                {inFlight.length === 0 ? (<p className="py-2 text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.no_payments_in_flight")}</p>) : inFlight.map((attempt) => (<div key={attempt.id} className="space-y-1.5 rounded-md border p-2.5" data-testid={`bill-attempt-${attempt.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5">
                            <span className="size-2 shrink-0 rounded-full bg-status-occupied"/>
                            <span className="truncate font-medium">{attempt.payerName ?? paymentMethodLabel(attempt.paymentMethod)}</span>
                        </span>
                        <span className="shrink-0 font-semibold tabular-nums">{formatPrice(attempt.totalAmount, currency)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        {paymentMethodLabel(attempt.paymentMethod)}
                        {attempt.isShare ? ` · ${t("ui.react.features.staff.table_detail_workspace.share_of_split")}` : ""}
                        {attempt.createdAt ? ` · ${t("ui.react.features.staff.table_detail_workspace.waiting_for", { minutes: minutesSince(attempt.createdAt) })}` : ""}
                    </p>
                    {/* Digital attempts are resolved by the Stripe webhook, never by hand:
                        freeing the items while the session can still succeed is how a
                        table gets charged twice. */}
                    {attempt.paymentMethod === "digital_in_app" ? (<p className="text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.digital_payment_auto_confirmed")}</p>) : (<div className="flex flex-wrap gap-2 pt-0.5">
                        <Button type="button" size="sm" variant="outline" disabled={resourceIsLoading(staffMutationResourceKey("attempt", attempt.id))} onClick={() => onResolvePayment(attempt.id, "succeeded")} data-testid={`bill-attempt-confirm-${attempt.id}`}>{t("ui.react.features.staff.table_detail_workspace.confirm_payment")}</Button>
                        <Button type="button" size="sm" variant="destructive" disabled={resourceIsLoading(staffMutationResourceKey("attempt", attempt.id))} onClick={() => onResolvePayment(attempt.id, "failed")} data-testid={`bill-attempt-cancel-${attempt.id}`}>{t("ui.react.features.staff.table_detail_workspace.cancel_attempt")}</Button>
                    </div>)}
                </div>))}
            </TabsContent>

            <TabsContent value="settled" className="mt-2 space-y-2">
                {settled.length === 0 ? (<p className="py-2 text-xs text-muted-foreground">{t("ui.react.features.staff.table_detail_workspace.no_settled_payments")}</p>) : settled.map((payment) => (<SettledPaymentRow key={payment.id} payment={payment} currency={currency} refundReasons={refundReasons} canRefund={canRefund} timeZone={timeZone}/>))}
            </TabsContent>
        </Tabs>
    </section>)
}

/**
 * A payment that already landed — and the way back out of it.
 *
 * Shared by the open account and the history card because a refund is asked for
 * on both sides of a bill closing, and two copies of this row is how the two
 * places end up offering different actions on the same money.
 */
function SettledPaymentRow({ payment, currency, refundReasons, canRefund, timeZone }: {
    payment: TableAccountSettledPayment
    currency: string
    refundReasons: RefundReasonOption[]
    canRefund: boolean
    timeZone?: string
}) {
    return (<div className="space-y-2 rounded-md border p-2.5" data-testid={`bill-settled-${payment.id}`}>
        <div className="flex items-baseline justify-between gap-2">
            <span className="min-w-0">
                <span className="block truncate font-medium">{payment.payerName ?? paymentMethodLabel(payment.paymentMethod)}</span>
                <span className="block text-xs text-muted-foreground">
                    {paymentMethodLabel(payment.paymentMethod)}
                    {payment.isShare ? ` · ${t("ui.react.features.staff.table_detail_workspace.share_of_split")}` : ""}
                </span>
            </span>
            <span className="shrink-0 font-semibold tabular-nums">{formatPrice(payment.totalAmount, currency)}</span>
        </div>
        {canRefund && payment.refundEligibility ? (<RefundAction paymentAttemptId={payment.id} eligibility={payment.refundEligibility} issued={payment.refunds ?? []} currency={currency} reasons={refundReasons} timeZone={timeZone}/>) : null}
    </div>)
}

function minutesSince(iso: string): number {
    const elapsed = Date.now() - new Date(iso).getTime()
    return Math.max(0, Math.floor(elapsed / 60000))
}
